/**
 * Mock budget context data for demo mode. Live mode calls
 * `GET /api/projects/{projectId}/budget-context` via `useBudgetContext`.
 */
import type { BudgetContext } from '@/services/api/budgetContextApi';

export type { BudgetContext };
export { resolveInitialHtc } from '@/services/api/budgetContextApi';

/**
 * Specific overrides keyed by `{projectCode}-{costCentreName}`.
 * These take priority over the generated fallback.
 */
const mockBudgetContextOverrides: Record<string, BudgetContext> = {
  // Spec example rows (used when demo data happens to match)
  'WSL6257-Design': {
    budgetHours: 100,
    loggedHours: 42,
    remainingHours: 58,
    isAllocated: true,
    recentSavedHtc: null,
  },
  'WSL6257-Projects': {
    budgetHours: 60,
    loggedHours: 60,
    remainingHours: 0,
    isAllocated: true,
    recentSavedHtc: 20,
  },
  'WSL6300-Admin': {
    budgetHours: 0,
    loggedHours: 8,
    remainingHours: 0,
    isAllocated: false,
    recentSavedHtc: null,
  },
};

/**
 * Simple deterministic hash so demo values are consistent across renders
 * but vary meaningfully between different project/cost-centre combinations.
 */
function simpleHash(str: string): number {
  let h = 0;
  for (let i = 0; i < str.length; i++) {
    h = (Math.imul(31, h) + str.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
}

/**
 * Generates a plausible mock BudgetContext for any project/cost-centre pair.
 * Values are deterministic (same key always returns the same numbers).
 *
 * ~70% of rows have an allocation, ~30% don't.
 * ~20% of allocated rows carry forward a recent saved HTC value.
 */
function generateMockBudgetContext(key: string): BudgetContext {
  const hash = simpleHash(key);

  const isAllocated = hash % 10 < 7; // 70% allocated

  if (!isAllocated) {
    const logged = (hash % 40) + 2;
    return {
      budgetHours: 0,
      loggedHours: logged,
      remainingHours: 0,
      isAllocated: false,
      recentSavedHtc: null,
    };
  }

  // Allocated — vary budget between 40 and 200 hrs
  const budget = ((hash % 17) + 4) * 10; // 40, 50, … 200
  // Logged between 0% and 110% of budget (some rows over-budget)
  const loggedPct = (simpleHash(key + 'log') % 110);
  const logged = Math.min(Math.round((budget * loggedPct) / 100), budget + 20);
  const remaining = Math.max(budget - logged, 0);

  // ~20% chance of a recent saved HTC value
  const hasRecent = simpleHash(key + 'recent') % 10 < 2;
  const recentSavedHtc = hasRecent
    ? Math.round(remaining * (0.5 + (simpleHash(key + 'val') % 50) / 100))
    : null;

  return {
    budgetHours: budget,
    loggedHours: logged,
    remainingHours: remaining,
    isAllocated: true,
    recentSavedHtc,
  };
}

/**
 * Looks up budget context for a given project + cost centre combination.
 * Returns a specific override if one exists, otherwise generates deterministic
 * mock data so the tooltip and pre-population work for all demo rows.
 */
export function getBudgetContext(
  projectCode: string | undefined,
  costCentreName: string | undefined,
): BudgetContext | null {
  // Need at least one identifier to generate meaningful context.
  if (!projectCode && !costCentreName) return null;
  const key = `${projectCode ?? 'no-project'}-${costCentreName ?? 'no-cc'}`;
  return mockBudgetContextOverrides[key] ?? generateMockBudgetContext(key);
}
